import React, { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify"
import {
  Button,
  Empty,
  notification,
  Spin,
  Tag,
  Modal,
  Input,
  Tooltip,
  Alert,
} from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CopyOutlined,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
  LockOutlined,
} from "@ant-design/icons";
import AttestationModal from "./AttestationModal";
import starIcon from "./christmas-stars.png";
import axiosInstance from "../../../core/index-core";
import { metrics } from "../../../metrics/client";
import { sanitizeNoteFieldValue } from "../../../library/clinicalParsing";
import { resolveActivePageIndex } from "../../../library/notePaging";
import { CustomButton } from "./SessionSidebar";

const { TextArea } = Input;

const n = (v, d = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : d;
};

const AIGeneratedTag = () => (
  <Tag color="blue">
    <div style={{ display: "flex", gap: 5, padding: 2, width: 20, height: 20 }}>
      <img src={starIcon} alt="Wand Icon" width="14" height="14" />
    </div>
  </Tag>
);

const sanitizeLabel = (s = "") =>
  String(s)
    .replace(/\[FIELD\]/g, "")
    .replace(/\s*:\s*$/, "")
    .trim();

const toTitle = (s = "") =>
  sanitizeLabel(
    String(s)
      .replace(/[_\-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
  )
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getLabelForControl = (control) => {
  if (control?.printFormat?.includes("[FIELD]")) {
    return sanitizeLabel(control.printFormat.replace("[FIELD]", ""));
  }
  return sanitizeLabel(
    control?.field?.displayName || control?.field?.fieldName || ""
  );
};

const ControlRenderer = ({
  control,
  fieldMappings,
  setFormUpdates,
  page_name,
  connectionState = 'connected',
  isOnline = true,
  isEhrSubmitted = false,
  isValidAppointment = true,
}) => {
  const isOffline = !isOnline || isEhrSubmitted || (isValidAppointment === false); // Disable when offline OR EHR submitted OR invalid appointment
  const fieldName = control?.field?.fieldName;

  // resolve live value from mappings + user edits (controlled)
  // sanitizeNoteFieldValue converts literal "\n" escape sequences from the backend
  // into real newline characters so textareas render proper line breaks.
  const initialValue = useMemo(() => {
    if (!fieldName) return sanitizeNoteFieldValue(control?.value ?? "");
    const raw = fieldMappings?.[fieldName];
    return sanitizeNoteFieldValue((raw ?? control?.value ?? "").toString());
  }, [fieldMappings, fieldName, control?.value]);

  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    // sync when latest AI note / mappings arrive
    setValue(initialValue);
  }, [initialValue]);

  const pushUpdate = (next) => {
    if (!fieldName) return;
    setFormUpdates((prev) => ({
      ...prev,
      pages: prev?.pages?.map((x) =>
        x?.page_name === page_name
          ? {
              ...x,
              attributes: [
                ...x?.attributes?.filter((a) => a?.name !== fieldName),
                { name: fieldName, value: next },
              ],
            }
          : x
      ),
    }));
  };

  const commonStyle = {
    position: "absolute",
    left: n(control.leftLoc),
    top: n(control.topLoc),
    width: n(control.width) || "auto",
    height: n(control.height) || "auto",
    outline: "none",
    fontSize: control.fontSize,
    fontFamily: control.fontFamily,
    fontWeight: control.fontProperties === 1 ? "bold" : "normal",
    boxShadow: control?.aiGenerated ? "0 2px 8px rgba(59, 130, 246, 0.5)" : "",
  };

  // Caption-only blocks
  if (control.caption && !control.field) {
    return (
      <div style={commonStyle}>
        <label>{control.caption}</label>
      </div>
    );
  }

  // === Textarea-like (1, 11) ===
  if (
    control.field &&
    (control.controlType === 1 || control.controlType == 11)
  ) {
    const w = n(control.width, 280);
    const h = n(control.height, 80);
    return (
      <>
        <textarea
          disabled={isOffline}
          style={{
            ...commonStyle,
            width: w,
            height: h,
            border: "1px solid #ccc",
            padding: 4,
            boxSizing: "border-box",
            whiteSpace: "pre-wrap",
            resize: "none",
            backgroundColor: control?.isRequired ? "#ffffe0" : "#fff",
          }}
          placeholder={
            control.printFormat?.includes("[FIELD]")
              ? control.printFormat.replace("[FIELD]", "")
              : ""
          }
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            pushUpdate(e.target.value);
          }}
        />
        {control?.aiGenerated ? (
          <div
            style={{
              position: "absolute",
              left: n(control.leftLoc) + n(control.width, 0) + 20,
              top: n(control.topLoc),
            }}
          >
            <AIGeneratedTag />
          </div>
        ) : null}
      </>
    );
  }

  // === Single checkbox (3 without options) ===
  if (control.controlType === 3 && !control.field?.listOptions) {
    const label = getLabelForControl(control) || toTitle(fieldName);
    const boolFromStr = (s) =>
      ["1", "true", "yes", "y", "on", "checked", "x"].includes(
        String(s ?? "")
          .trim()
          .toLowerCase()
      );
    const [checked, setChecked] = useState(boolFromStr(value));
    useEffect(() => setChecked(boolFromStr(value)), [value]);

    const onToggle = () => {
      const next = !checked;
      setChecked(next);
      setValue(next ? "true" : "false");
      pushUpdate(next ? "true" : "false");
    };

    return (
      <div style={commonStyle}>
        <label style={{ display: "block", cursor: "pointer" }}>
          <input type="checkbox" disabled={isOffline} checked={checked} onChange={onToggle} />{" "}
          {label}
        </label>
      </div>
    );
  }

// === Multi-checkbox (3 with options) ===
if (control.controlType === 3 && control.field?.listOptions) {
  const norm = (s) => String(s ?? "").trim().toLowerCase();

  const toSlug = (s) =>
    String(s ?? "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const fieldLabelFallback = (() => {
    const fn = control?.field?.fieldName || "";
    const dn = control?.field?.displayName || "";
    const prettyFromFN = fn
      ? fn.replace(/^present[_\-\s]*/i, "").replace(/[_\-]+/g, " ")
      : "";
    const base = dn || prettyFromFN || fn || "Option";
    return toTitle(base);
  })();

  const rawOpts = Array.isArray(control.field.listOptions)
    ? control.field.listOptions
    : [];
  const seenVals = new Set();
  const opts = rawOpts.map((o, i) => {
    let label =
      o.label ?? o.displayName ?? o.text ?? o.caption ?? o.value ?? "";
    const isGeneric = (s) =>
      !s ||
      /^checkbox_option_/i.test(s) ||
      /^option\d*$/i.test(s) ||
      /^\d+$/.test(s);
    if (isGeneric(label)) label = fieldLabelFallback;

    let valueOpt = o.value ?? label;
    if (!valueOpt || /^checkbox_option_/i.test(String(valueOpt)) || seenVals.has(valueOpt)) {
      valueOpt = `${toSlug(label) || "opt"}__${i}`;
    }
    seenVals.add(valueOpt);
    return { label, value: valueOpt };
  });

  // ---- special cases / helpers ----
  const fieldName = control?.field?.fieldName || "";
  const fnLower   = fieldName.toLowerCase();

  // Treat these fields as single binary flags that must store "1"/"0"
  const isPresentFlag = /^present_/i.test(fnLower);
  const isTxBinary    = /^tx(recommended|modality)_/i.test(fnLower);
  const isSingleBinaryFlag = isPresentFlag || isTxBinary;

  // detect the 3-option "Present at session" group -> store {"patient":"1",...}
  const isPresentAtSessionGroup = () => {
    const byFieldName =
      fnLower.includes("present") && fnLower.includes("session");
    const L = new Set(opts.map(o => norm(o.label ?? o.value)));
    const byLabels =
      L.has("patient") &&
      (L.has("significant other") || L.has("significant_other")) &&
      (L.has("parent/guardian") || L.has("parent guardian") || L.has("parent_guardian"));
    return byFieldName || byLabels;
  };

  const toCanonicalKey = (labelOrValue = "") => {
    const s = norm(labelOrValue);
    if (s === "patient") return "patient";
    if (s === "significant other" || s === "significant_other") return "significant_other";
    if (s === "parent/guardian" || s === "parent guardian" || s === "parent_guardian") return "parent_guardian";
    return s.replace(/[^a-z0-9]+/g, "_");
  };

  // which option means "checked" for single-binary flags
  const presentTail = fnLower.replace(/^present_/, "").replace(/_/g, " ");
  const targetOpt = isSingleBinaryFlag
    ? (isPresentFlag
        ? (opts.find(o => norm(o.label ?? o.value) === presentTail) || opts[0] || null)
        : (opts[0] || null))
    : null;

  // ---- current value -> selected list ----
  const parseSelected = (val) => {
    // single-binary flags read "1"/"0"
    if (isSingleBinaryFlag && targetOpt) {
      const s = String(val ?? "").trim().toLowerCase();
      const on = s === "1" || s === "true" || s === "yes" || s === "y";
      return on ? [targetOpt.value] : [];
    }
    // JSON payload for present-at-session group
    if (typeof val === "string" && val.trim().startsWith("{")) {
      try {
        const obj = JSON.parse(val);
        return opts
          .filter(o => obj[toCanonicalKey(o.label ?? o.value)] === "1")
          .map(o => o.value);
      } catch {/* ignore */}
    }
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      return val.split(",").map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  const initialSelected = useMemo(() => {
    const tokens = parseSelected(value);
    return tokens
      .map((t) => {
        const hit = opts.find(
          (o) => norm(o.value) === norm(t) || norm(o.label) === norm(t)
        );
        return hit ? hit.value : t;
      })
      .filter(Boolean);
  }, [value, JSON.stringify(opts)]);

  const [selected, setSelected] = useState(initialSelected);
  useEffect(() => setSelected(initialSelected), [initialSelected]);

  const toggle = (val) => {
    // single-binary flags push only "1"/"0"
    if (isSingleBinaryFlag && targetOpt) {
      const willBeChecked = !selected.includes(targetOpt.value);
      const nextSel = willBeChecked ? [targetOpt.value] : [];
      const out = willBeChecked ? "1" : "0";
      setSelected(nextSel);
      setValue(out);
      pushUpdate(out);
      return;
    }

    // normal multi-select path
    setSelected((prevSel) => {
      const next = prevSel.includes(val)
        ? prevSel.filter((v) => v !== val)
        : [...prevSel, val];

      // special 3-key JSON payload
      if (isPresentAtSessionGroup()) {
        const payload = { patient: "0", significant_other: "0", parent_guardian: "0" };
        opts.forEach((o) => {
          const key = toCanonicalKey(o.label ?? o.value);
          payload[key] = next.includes(o.value) ? "1" : "0";
        });
        const out = JSON.stringify(payload);
        setValue(out);
        pushUpdate(out);
        return next;
      }

      // default: CSV for generic multi-checkbox groups
      const nextStr = next.join(", ");
      setValue(nextStr);
      pushUpdate(nextStr);
      return next;
    });
  };

  return (
    <div style={commonStyle}>
      {opts.map((opt, idx) => (
        <label key={idx} style={{ display: "block", cursor: "pointer" }}>
          <input
            type="checkbox"
            disabled={isOffline}
            checked={selected.includes(opt.value)}
            onChange={() => toggle(opt.value)}
          />{" "}
          {opt.label}
        </label>
      ))}
    </div>
  );
}

  if (
    control.controlType === 9 &&
    control.field?.listOptions &&
    control?.field?.fieldName == "MH_VisitType"
  ) {
    const [selectedValue, setSelectedValue] = useState("");

    const norm = (s) =>
      String(s ?? "")
        .trim()
        .toLowerCase();
    const toSlug = (s) =>
      String(s ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    const fieldLabelFallback = (() => {
      const fn = control?.field?.fieldName || "";
      const dn = control?.field?.displayName || "";
      const prettyFromFN = fn
        ? fn.replace(/^present[_\-\s]*/i, "").replace(/[_\-]+/g, " ")
        : "";
      const base = dn || prettyFromFN || fn || "Option";
      return toTitle(base);
    })();

    const handleChange = (event, idx) => {
      const value = event.target.value;
      setSelectedValue(value);
      pushUpdate(String(idx + 1));
    };
    

    const rawOpts = Array.isArray(control.field.listOptions)
      ? control.field.listOptions
      : [];
    const seenVals = new Set();
    const opts = rawOpts.map((o, i) => {
      let label =
        o.label ?? o.displayName ?? o.text ?? o.caption ?? o.value ?? "";
      const isGeneric = (s) =>
        !s ||
        /^checkbox_option_/i.test(s) ||
        /^option\d*$/i.test(s) ||
        /^\d+$/.test(s);
      if (isGeneric(label)) label = fieldLabelFallback;
      let valueOpt = o.value ?? label;
      if (
        !valueOpt ||
        /^checkbox_option_/i.test(String(valueOpt)) ||
        seenVals.has(valueOpt)
      ) {
        valueOpt = `${toSlug(label) || "opt"}__${i}`;
      }
      seenVals.add(valueOpt);
      return { label, value: valueOpt };
    });

    // parse current value to array
    const parseSelected = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === "string")
        return val
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [];
    };

    const initialSelected = useMemo(() => {
      const tokens = parseSelected(value);
      return tokens
        .map((t) => {
          const hit = opts.find(
            (o) => norm(o.value) === norm(t) || norm(o.label) === norm(t)
          );
          return hit ? hit.value : t;
        })
        .filter(Boolean);
    }, [value, JSON.stringify(opts)]);

    // const [selected, setSelected] = useState(initialSelected);
    // useEffect(() => setSelected(initialSelected), [initialSelected]);

    const toggle = (val) => {
      setSelected((prevSel) => {
        const next = prevSel.includes(val)
          ? prevSel.filter((v) => v !== val)
          : [...prevSel, val];
        const nextStr = next.join(", ");
        setValue(nextStr);
        pushUpdate(nextStr);
        return next;
      });
    };

    return (
      <div style={commonStyle}>
        <div className="flex flex-wrap gap-6">
          {control?.field?.listOptions
            .sort((a, b) => a.order - b.order)
            .map((option,idx) => (
              <label
                key={option.id}
                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors"
              >
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  disabled={isOffline}
                  checked={selectedValue === option.value}
                  onChange={(e) => handleChange(e, idx)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-gray-900 select-none">
                  {option.value}
                </span>
              </label>
            ))}
        </div>
      </div>
    );
  }

  // if (control.controlType === 9 && control.field?.listOptions) {
  //   const norm = (s) =>
  //     String(s ?? "")
  //       .trim()
  //       .toLowerCase();
  //   const toSlug = (s) =>
  //     String(s ?? "")
  //       .trim()
  //       .toLowerCase()
  //       .replace(/[^a-z0-9]+/g, "-")
  //       .replace(/(^-|-$)/g, "");

  //   const fieldLabelFallback = (() => {
  //     const fn = control?.field?.fieldName || "";
  //     const dn = control?.field?.displayName || "";
  //     const prettyFromFN = fn
  //       ? fn.replace(/^present[_\-\s]*/i, "").replace(/[_\-]+/g, " ")
  //       : "";
  //     const base = dn || prettyFromFN || fn || "Option";
  //     return toTitle(base);
  //   })();

  //   const rawOpts = Array.isArray(control.field.listOptions)
  //     ? control.field.listOptions
  //     : [];
  //   const seenVals = new Set();
  //   const opts = rawOpts.map((o, i) => {
  //     let label =
  //       o.label ?? o.displayName ?? o.text ?? o.caption ?? o.value ?? "";
  //     const isGeneric = (s) =>
  //       !s ||
  //       /^checkbox_option_/i.test(s) ||
  //       /^option\d*$/i.test(s) ||
  //       /^\d+$/.test(s);
  //     if (isGeneric(label)) label = fieldLabelFallback;
  //     let valueOpt = o.value ?? label;
  //     if (
  //       !valueOpt ||
  //       /^checkbox_option_/i.test(String(valueOpt)) ||
  //       seenVals.has(valueOpt)
  //     ) {
  //       valueOpt = `${toSlug(label) || "opt"}__${i}`;
  //     }
  //     seenVals.add(valueOpt);
  //     return { label, value: valueOpt };
  //   });

  //   // parse current value to array
  //   const parseSelected = (val) => {
  //     if (Array.isArray(val)) return val;
  //     if (typeof val === "string")
  //       return val
  //         .split(",")
  //         .map((s) => s.trim())
  //         .filter(Boolean);
  //     return [];
  //   };

  //   const initialSelected = useMemo(() => {
  //     const tokens = parseSelected(value);
  //     return tokens
  //       .map((t) => {
  //         const hit = opts.find(
  //           (o) => norm(o.value) === norm(t) || norm(o.label) === norm(t)
  //         );
  //         return hit ? hit.value : t;
  //       })
  //       .filter(Boolean);
  //   }, [value, JSON.stringify(opts)]);

  //   const [selected, setSelected] = useState(initialSelected);
  //   useEffect(() => setSelected(initialSelected), [initialSelected]);

  //   const toggle = (val) => {
  //     setSelected((prevSel) => {
  //       const next = prevSel.includes(val)
  //         ? prevSel.filter((v) => v !== val)
  //         : [...prevSel, val];
  //       const nextStr = next.join(", ");
  //       setValue(nextStr);
  //       pushUpdate(nextStr);
  //       return next;
  //     });
  //   };

  //   return (
  //     <div style={commonStyle}>
  //       {opts.map((opt, idx) => (
  //         <label key={idx} style={{ display: "block", cursor: "pointer" }}>
  //           <input
  //             type="checkbox"
  //             checked={selected.includes(opt.value)}
  //             onChange={() => toggle(opt.value)}
  //           />{" "}
  //           {opt.label}
  //         </label>
  //       ))}
  //     </div>
  //   );
  // }

  // === Multiline 5 ===
  if (control.controlType === 5) {
    const w = n(control.width, 280);
    const h = n(control.height, 80);
    return (
      <div style={{ ...commonStyle, border: "none", boxShadow: "none" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <textarea
            disabled={isOffline}
            style={{
              width: w,
              height: h,
              backgroundColor: control?.isRequired ? "#ffffe0" : "#fff",
              outline: "none",
              border: "1px solid #ccc",
              padding: 4,
              resize: "none",
              boxShadow: control?.aiGenerated
                ? "0 2px 8px rgba(59, 130, 246, 0.5)"
                : "",
            }}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              pushUpdate(e.target.value);
            }}
          />
          {control?.aiGenerated ? <AIGeneratedTag /> : null}
        </div>
      </div>
    );
  }

  // === Generic fallback ===
  if (control.field) {
    // dropdown?
    const normalizeOptions = (opts) => {
      if (Array.isArray(opts)) return opts;
      if (typeof opts === "string") {
        return opts
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((x, i) => ({ label: x, value: x || `opt_${i}` }));
      }
      return [];
    };
    const opts = normalizeOptions(control?.field?.listOptions);

    const isYesNoSet = (arr) => {
      if (!Array.isArray(arr)) return false;
      const norm = (s) =>
        String(s ?? "")
          .trim()
          .toLowerCase();
      const labels = arr.map((o) => norm(o.label ?? o.value));
      const set = new Set(labels);
      return set.has("yes") && set.has("no") && set.size === 2;
    };

    if (opts.length) {
      if (isYesNoSet(opts)) {
        const coerceYN = (raw) => {
          const s = String(raw ?? "")
            .trim()
            .toLowerCase();
          if (["1", "yes", "y", "true", "on"].includes(s)) return 1;
          if (["0", "no", "n", "false", "off"].includes(s)) return 0;
          return null;
        };

        const [yn, setYn] = useState(coerceYN(value));
        useEffect(() => setYn(coerceYN(value)), [value]);

        const onChangeYN = (next) => {
          setYn(next);
          const out = next == null ? "" : String(next);
          setValue(out);
          pushUpdate(out);
        };

        return (
          <div
            style={{
              ...commonStyle,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name={`${fieldName || "yn"}__${control?.id || ""}`}
                disabled={isOffline}
                checked={yn === 1}
                onChange={() => onChangeYN(1)}
              />
              Yes
            </label>

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name={`${fieldName || "yn"}__${control?.id || ""}`}
                disabled={isOffline}
                checked={yn === 0}
                onChange={() => onChangeYN(0)}
              />
              No
            </label>

            <button
              type="button"
              disabled={isOffline}
              onClick={() => onChangeYN(null)}
              style={{
                marginLeft: 8,
                padding: "2px 6px",
                border: "1px solid #ccc",
                background: "#fff",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 12,
              }}
              title="Clear selection"
            >
              Clear
            </button>

            {control?.aiGenerated ? (
              <div style={{ marginLeft: 8 }}>
                <AIGeneratedTag />
              </div>
            ) : null}
          </div>
        );
      }

      const placeholder = control.printFormat?.includes("[FIELD]")
        ? control.printFormat.replace("[FIELD]", "")
        : getLabelForControl(control) || toTitle(fieldName);

      return (
        <div style={commonStyle}>
          <select
            disabled={isOffline}
            style={{ width: n(control.width, 220), height: 32 }}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              pushUpdate(e.target.value);
            }}
          >
            <option value="">{placeholder || "Select..."}</option>
            {opts.map((o, idx) => (
              <option key={idx} value={o.value ?? o.label}>
                {o.label ?? o.value}
              </option>
            ))}
          </select>
          {control?.aiGenerated ? (
            <div style={{ marginTop: 6 }}>
              <AIGeneratedTag />
            </div>
          ) : null}
        </div>
      );
    }

    // input vs textarea by given height
    if (n(control?.height) >= 40) {
      return (
        <textarea
          disabled={isOffline}
          style={{
            ...commonStyle,
            width: n(control.width, 280),
            height: n(control.height, 80),
            border: "1px solid #ccc",
            padding: 4,
            boxSizing: "border-box",
            whiteSpace: "pre-wrap",
            resize: "none",
            backgroundColor: "#ffffe0",
          }}
          placeholder={
            control.printFormat?.includes("[FIELD]")
              ? control.printFormat.replace("[FIELD]", "")
              : getLabelForControl(control) || toTitle(fieldName)
          }
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            pushUpdate(e.target.value);
          }}
        />
      );
    }

    return (
      <input
        type="text"
        disabled={isOffline}
        style={{
          ...commonStyle,
          width: n(control.width, 280),
          height: n(control.height, 32),
          border: "1px solid #ccc",
          padding: "4px 6px",
          boxSizing: "border-box",
          backgroundColor: "#ffffe0",
        }}
        placeholder={
          control.printFormat?.includes("[FIELD]")
            ? control.printFormat.replace("[FIELD]", "")
            : getLabelForControl(control) || toTitle(fieldName)
        }
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          pushUpdate(e.target.value);
        }}
      />
    );
  }

  return null;
};
const DynamicForm2 = ({
  controls,
  fieldMappings,
  setFormUpdates,
  page_name,
  connectionState,
  isOnline,
  isEhrSubmitted,
}) => {
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {controls.map((control) => (
        <ControlRenderer
          key={control.id}
          control={control}
          fieldMappings={fieldMappings}
          setFormUpdates={setFormUpdates}
          page_name={page_name}
          connectionState={connectionState}
          isOnline={isOnline}
          isEhrSubmitted={isEhrSubmitted}
        />
      ))}
    </div>
  );
};

let timerId = null;


const AINotesView2 = ({
  isNoteSigned,
  fieldMappings,
  template,
  noteJSON,
  patient_id,
  session_id,
  clinician_name,
  session_date,
  fetchingNote,
  pollingNote,
  recordingParent,
  setNoteSubmitted,
  onNoteLocked,
  isNoteLocked,
  connectionState = 'connected',
  isOnline = true,
  isEhrSubmitted = false,
}) => {
  const isOffline = !isOnline || isEhrSubmitted; // Disable when offline OR EHR submitted
  const [collapsed, setCollapsed] = useState(true);
  const [menuItems, setMenuItems] = useState([{ id: 1, name: "Session" }]);
  const [selectedMenuItem, setSelectedMenuItem] = useState(0);
  const [formUpdates, setFormUpdates] = useState({});
  const [uploading, setUploading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  // Feedback state
  const [ratingCommitted, setRatingCommitted] = useState(null);
  const [commentsCommitted, setCommentsCommitted] = useState("");
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [ratingPending, setRatingPending] = useState(null);
  const [commentsPending, setCommentsPending] = useState("");




  useEffect(() => {
    const saveIntermediateState = function() {
      if(timerId) {
        clearTimeout(timerId);
      }

      timerId = setTimeout(async () => {
        try {
          await axiosInstance.put(`patient/${patient_id}/sessions/${session_id}/ai-note/draft`, {
            note_data: formUpdates,
          })
        }
        catch(e) {
          console.error(e);
        }
      }, 2000)
    }

    saveIntermediateState();
  }, [formUpdates])

  // fetch existing feedback when session changes
  useEffect(() => {
    const fetchFeedback = async () => {
      if (!patient_id || !session_id) return;
      try {
        const res = await axiosInstance.get(
          `patient/${patient_id}/sessions/${session_id}/notes/ai-feedback`,
          { validateStatus: (status) => status !== 401 }
        );

        if (res.status === 200 && res.data) {
          const r =
            typeof res.data.rating === "number" ? res.data.rating : null;
          setRatingCommitted(r);
          setCommentsCommitted(res.data.comments || "");
        } else if (res.status === 204) {
          setRatingCommitted(null);
          setCommentsCommitted("");
        }
      } catch (err) {
        console.error("Error fetching ai feedback:", err);
        notification.error({
          message: "Network error fetching ai feedback",
        });
      }
    };
    fetchFeedback();
  }, [patient_id, session_id]);

  // menu + formUpdates scaffold when note changes
  useEffect(() => {
    if (!noteJSON?.pages?.length) return;
    const menu =
      noteJSON.pages.map((x, index) => ({ id: index, name: x?.page_name })) ||
      [];
    setMenuItems(menu);
    const sessionIdx = menu.findIndex(
      (m) =>
        String(m.name || "")
          .trim()
          .toLowerCase() === "session" ||
        String(m.name || "")
          .trim()
          .toLowerCase() === "note"
    );
    setSelectedMenuItem(sessionIdx !== -1 ? sessionIdx : menu?.[0]?.id ?? 0);

    setFormUpdates({
      note_id: noteJSON?.note_id,
      pages:
        noteJSON.pages.map((x, i) => ({
          page_name: x?.page_name,
          page_index: i,
          attributes:
            x?.attributes?.map((a) => ({
              name: a?.field?.fieldName,
              value: a?.value,
            })) || [],
        })) || [],
    });
  }, [noteJSON]);

  const escapeHtml = (s = "") =>
    String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const buildPlainFromCopyModel = (m) =>
    m.rows
      .map(({ label, value }) => {
        const val = String(value ?? "--");
        return val.includes("\n") ? `${label}\n${val}` : `${label}: ${val}`;
      })
      .join("\n");

  const buildHTMLFromCopyModel = (m) => {
    const style = `
    <style>
      table.kv-table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: 13px; color: #111; }
      table.kv-table th, table.kv-table td { border: 1px solid #d9d9d9; padding: 6px 8px; vertical-align: top; }
      table.kv-table th { width: 240px; background: #f7f7f7; font-weight: 600; text-transform: uppercase; letter-spacing: .02em; }
      table.kv-table td { white-space: pre-wrap; }
    </style>`;
    let html = `${style}<table class="kv-table"><tbody>`;
    m.rows.forEach(({ label, value }) => {
      const safeVal = escapeHtml(String(value ?? "--")).replace(/\n/g, "<br>");
      html += `<tr><th>${escapeHtml(label)}</th><td>${safeVal}</td></tr>`;
    });
    html += `</tbody></table>`;
    return html;
  };

  const buildCopyModelForSelected = () => {
    const pageName = menuItems?.[selectedMenuItem]?.name;
    const page = noteJSON?.pages?.find((p) => p?.page_name === pageName);
    if (!page) return null;

    const seen = new Set();
    const captions = [];
    const kvRows = [];

    page?.attributes?.forEach((control) => {
      if (control?.caption && !control.field) {
        const cap = sanitizeLabel(control.caption);
        if (cap) captions.push(cap);
        return;
      }
      if (!control?.field) return;

      const fieldName = control?.field?.fieldName;
      if (!fieldName) return;

      let labelCandidate = getLabelForControl(control) || toTitle(fieldName);
      if (seen.has(fieldName.toLowerCase())) return;

      const raw = fieldMappings?.[fieldName] ?? control?.value ?? "";
      const value = String(raw ?? "").trim() ? sanitizeNoteFieldValue(String(raw)) : "--";
      kvRows.push({ label: labelCandidate, value });
      seen.add(fieldName.toLowerCase());
    });

    const rows = [];
    if (pageName) rows.push({ label: "Tab", value: pageName });

    const tableKeySet = new Set([
      ...kvRows.map((r) => r.label.toLowerCase()),
      "tab",
    ]);
    const filteredCaptions = captions
      .map((c) => c.replace(/\s*:\s*$/, ""))
      .filter((c) => {
        const n = c.trim().toLowerCase();
        if (!n || c.length <= 2) return false;
        if (tableKeySet.has(n)) return false;
        return true;
      });

    const top4 = filteredCaptions.slice(0, 4);
    if (top4.length >= 1) rows.push({ label: top4[0], value: "" });
    if (top4.length >= 3) rows.push({ label: top4[2], value: top4[1] });
    if (top4.length >= 4) rows.push({ label: top4[3], value: "" });

    rows.push(...kvRows);
    return { pageName, rows };
  };

  const handleCopy = async () => {
    const model = buildCopyModelForSelected();
    if (!model) {
      notification.warning({ message: "Nothing to copy on this page." });
      return;
    }
    const html = buildHTMLFromCopyModel(model);
    const text = buildPlainFromCopyModel(model);

    try {
      if (window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" }),
          }),
        ]);
        notification.success({ message: "Copied as Key/Value table." });
        return;
      }
      throw new Error("ClipboardItem not supported");
    } catch {
      const div = document.createElement("div");
      div.contentEditable = "true";
      div.style.position = "fixed";
      div.style.left = "-9999px";
      div.innerHTML = DOMPurify.sanitize(html);
      document.body.appendChild(div);
      const range = document.createRange();
      range.selectNodeContents(div);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand("copy");
      sel.removeAllRanges();
      document.body.removeChild(div);
      notification.success({
        message:
          "Copied (fallback). Paste into Word/Docs/Outlook to see a table.",
      });
    }
  };


// word counter
const countWords = (str = "") =>
String(str).trim() ? String(str).trim().split(/\s+/).length : 0;

//sentence counter
const countSentences = (str = "") =>
String(str)
  .split(/[.!?]+/)
  .map((s) => s.trim())
  .filter(Boolean).length;

const flattenPagesToMap = (pages = []) => {
const out = {};
pages.forEach((p) => {
  (p.attributes || []).forEach((a) => {
    const fieldName =
      a?.field?.fieldName ||
      a?.field_name ||
      a?.name;
    if (!fieldName) return;
    out[fieldName] = a?.value ?? "";
  });
});
return out;
};

//formUpdates
const flattenFormUpdatesToMap = (formUpdates = {}) => {
const out = {};
(formUpdates.pages || []).forEach((p) => {
  (p.attributes || []).forEach((a) => {
    if (!a?.name) return;
    out[a.name] = a.value ?? "";
  });
});
return out;
};

const roughSimilarity = (a = "", b = "") => {
const aLen = a.length;
const bLen = b.length;
if (!aLen && !bLen) return 1.0;
if (!aLen || !bLen) return 0.0;
let same = 0;
const min = Math.min(aLen, bLen);
for (let i = 0; i < min; i++) {
  if (a[i] === b[i]) same++;
}
return same / Math.max(aLen, bLen);
};

const getAiNotePayload = function() {
  if (!patient_id || !session_id || !noteJSON?.note_id) {
    console.warn("metrics: missing patient/session/note id, skipping");
    return;
  }

  const originalMap = flattenPagesToMap(noteJSON.pages || []);
  const editedMap = flattenFormUpdatesToMap(formUpdates || {});

  const originalText = Object.values(originalMap).join(" ");
  const editedText = Object.values(editedMap).join(" ");

  const ehr_word_count = countWords(originalText);
  const clinician_word_count = countWords(editedText);

  const changedFields = [];
  const changedDetails = {};

  const allFieldNames = new Set([
    ...Object.keys(originalMap),
    ...Object.keys(editedMap),
  ]);

  allFieldNames.forEach((field) => {
    const orig = originalMap[field] ?? "";
    const edit = editedMap[field] ?? "";

    if (orig !== edit) {
      changedFields.push(field);

      const origLen = countWords(orig);
      const editLen = countWords(edit);

      let modificationDesc = "Clinician revised or corrected the field content.";
      if (editLen > origLen) {
        modificationDesc = "Clinician added additional information.";
      } else if (editLen < origLen) {
        modificationDesc = "Clinician removed or condensed information.";
      }

      changedDetails[field] = {
        original_length: origLen,
        edited_length: editLen,
        modifications: [modificationDesc],
      };
    }
  });

  // crude diffs for counts
  const words_added = Math.max(clinician_word_count - ehr_word_count, 0);
  const words_removed = Math.max(ehr_word_count - clinician_word_count, 0);

  const sentences_added = Math.max(
    countSentences(editedText) - countSentences(originalText),
    0
  );
  const sentences_removed = Math.max(
    countSentences(originalText) - countSentences(editedText),
    0
  );

  const similarity_ratio = roughSimilarity(originalText, editedText);

  const payload = {
    patient_id: Number(patient_id),
    session_id: Number(session_id),
    note_id: Number(noteJSON?.note_id),
    ehr_word_count,
    clinician_word_count,
    words_added,
    words_removed,
    sentences_added,
    sentences_removed,
    similarity_ratio,
    fields_modified: {
      fields: changedFields,
      field_count: changedFields.length,
      details: changedDetails,
    },
  };
}

const sendClinicianEditsMetrics = async () => {
  
  const payload = getAiNotePayload();

  try {
    await axiosInstance.post(
      `/api/metrics/clinician-edits`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  } catch (err) {
    console.error("metrics: failed posting clinician edits", err);
  }
};


const handleAttestationSuccess = async () => {
  await sendClinicianEditsMetrics();
  setModalVisible(false);
};
  // Clamp the active page index so an out-of-range selectedMenuItem (its
  // initial value, or a stale value left by a previously-viewed multi-page
  // note) can never render an empty page on a note that has pages. This is the
  // fix for the WL blank-note bug (aispire-ui#8): a weight-loss note has a
  // single page, so the initial index of 1 used to yield 0 controls until a
  // post-paint effect corrected it, which flickered/stuck blank on remount.
  const activePageIndex = resolveActivePageIndex(
    noteJSON?.pages?.length || 0,
    selectedMenuItem
  );
  const currentPageName = noteJSON?.pages?.[activePageIndex]?.page_name;
  const currentControls = noteJSON?.pages?.[activePageIndex]?.attributes || [];

  if (recordingParent)
    return (
      <Empty description="AI notes will be generated once the recording is stopped." />
    );

  if (uploading || fetchingNote || pollingNote)
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
        <p className="mt-4 text-gray-500 text-sm font-medium">
          Loading notes...
        </p>
      </div>
    );

  if (!noteJSON?.pages?.length) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#555",
          fontSize: 16,
        }}
      >
        <Empty description="If no note appears after the generation, try refreshing the page" />
      </div>
    );
  }

  return (
    <div style={{ height: "calc(100% - 80px)" }}>
      {fetchingNote || pollingNote ? (
        <div
          style={{
            display: "flex",
            width: "100%",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
          <p className="mt-4 text-gray-500 text-sm font-medium">
            Loading note...
          </p>
        </div>
      ) : (
        <>
          <div className="flex w-full rounded-md" style={{ height: "100%" }}>
            {!collapsed && (
              <div
                className="w-1/4 pr-6 border-r border-gray-200 sticky top-6 self-start h-fit"
                style={{
                  height: "100%",
                  overflow: "auto",
                  scrollbarWidth: "none",
                }}
              >
                <ul className="space-y-2 text-sm font-medium">
                  {menuItems.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedMenuItem(index)}
                      className={`px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                        selectedMenuItem === index
                          ? "bg-gray-100 text-gray-900 border-gray-400 shadow-md font-semibold"
                          : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 hover:border-gray-400 hover:shadow-md"
                      }`}
                    >
                      {item?.name}
                    </div>
                  ))}
                </ul>
              </div>
            )}

            <div
              className={`${
                collapsed ? "w-full" : "w-3/4"
              } relative transition-all duration-300 ease-in-out border border-gray-200`}
              style={{ background: "#f6f6f6", borderRadius: 12 }}
            >
              {noteJSON?.pages && (
                <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
                  {collapsed ? (
                    <Button
                      icon={<MenuUnfoldOutlined />}
                      onClick={() => setCollapsed(!collapsed)}
                      size="small"
                      className="hover:bg-blue-100 font-medium shadow-sm hover:shadow-md transition-all duration-200 rounded-lg"
                    >
                      Expand Menu
                    </Button>
                  ) : (
                    <div className=" border-b border-gray-100">
                      <Button
                        icon={<MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                        size="small"
                        className="bg-gray-50 hover:bg-gray-100 border-gray-300 text-gray-700 font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        Collapse Menu
                      </Button>
                    </div>
                  )}
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleCopy}
                    size="small"
                    className="bg-white hover:bg-gray-50 border-gray-300 text-gray-700 font-medium shadow-sm hover:shadow-md transition-all duration-200 rounded-lg"
                  >
                    Copy
                  </Button>
                </div>
              )}

              {/* Canvas scroll area — use fixed left padding so absolute positions align */}
              <div
                className="pt-12"
                style={{
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "auto",
                  paddingLeft: "20%", // FIX: avoid percent padding that distorts absolute coords
                  scrollbarWidth: "thin",
                }}
              >
                <DynamicForm2
                  fieldMappings={fieldMappings}
                  setFormUpdates={setFormUpdates}
                  page_name={currentPageName}
                  controls={currentControls}
                  connectionState={connectionState}
                  isOnline={isOnline}
                  isEhrSubmitted={isEhrSubmitted}
                />
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex justify-end items-center gap-3">
              <Tooltip title="Like">
                <Button
                  type={ratingCommitted === 1 ? "primary" : "default"}
                  icon={
                    ratingCommitted === 1 ? <LikeFilled /> : <LikeOutlined />
                  }
                  onClick={() => {
                    setRatingPending(1);
                    setCommentsPending(commentsCommitted || "");
                    setFeedbackModalOpen(true);
                  }}
                  disabled={fetchingNote || pollingNote || isOffline}
                />
              </Tooltip>

              <Tooltip title="Dislike">
                <Button
                  type={ratingCommitted === 0 ? "primary" : "default"}
                  danger={ratingCommitted === 0}
                  icon={
                    ratingCommitted === 0 ? (
                      <DislikeFilled />
                    ) : (
                      <DislikeOutlined />
                    )
                  }
                  onClick={() => {
                    setRatingPending(0);
                    setCommentsPending(commentsCommitted || "");
                    setFeedbackModalOpen(true);
                  }}
                  disabled={fetchingNote || pollingNote || isOffline}
                />
              </Tooltip>

              {isNoteLocked && (
                <Tooltip title="Note has been submitted to EHR and is locked">
                  <LockOutlined style={{ 
                    fontSize: 16, 
                    color: '#8c8c8c',
                    marginRight: -8
                  }} />
                </Tooltip>
              )}
              
              <CustomButton
                onClick={() => setModalVisible(true)}
                size="large"
                disabled={fetchingNote || pollingNote || isNoteSigned || isNoteLocked || isOffline}
                text="Send to EHR"
                style={{
                  width: "fit-content",
                  opacity: isNoteLocked ? 0.5 : 1,
                  cursor: isNoteLocked ? 'not-allowed' : 'pointer'
                }}
              />
            </div>
          </div>
        </>
      )}

      <AttestationModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleAttestationSuccess}
        patient_id={patient_id}
        setNoteSubmitted={setNoteSubmitted}
        session_id={session_id}
        noteJSON={noteJSON}
        formUpdates={formUpdates}
        clinicianName={clinician_name}
        sessionDate={session_date}
        onNoteLocked={onNoteLocked}
      />

      {/* Centered feedback modal */}
      <Modal
        centered
        open={feedbackModalOpen}
        title={ratingPending === 1 ? "Like AI Notes" : "Dislike AI Notes"}
        onCancel={() => setFeedbackModalOpen(false)}
        onOk={async () => {
          if (ratingPending !== 0 && ratingPending !== 1) {
            notification.warning({ message: "Please choose like or dislike." });
            return;
          }

          // setRatingCommitted(ratingPending);
          // setFeedbackModalOpen(false);

          setFeedbackSubmitting(true);
          try {
            const payload = {
              note_id: Number(noteJSON?.note_id || 0),
              rating: ratingPending,
              comments: commentsPending || "",
            };
            const res = await axiosInstance.post(
              `patient/${patient_id}/sessions/${session_id}/notes/ai-feedback`,
              payload,
              { validateStatus: (status) => status !== 401 }
            );
            if (res.status === 200 && res.data) {
              setRatingCommitted(ratingPending);
              setCommentsCommitted(commentsPending || "");
              notification.success({ message: "Feedback submitted" });
              setFeedbackModalOpen(false);
            } else {
              notification.error({
                message: "Couldn't submit feedback",
                description: `Server returned ${res.status}`,
              });
            }
          } catch (err) {
            console.error('[AINotesView] Feedback submission failed:', err);
            notification.error({
              message: "Network error submitting feedback",
            });
            // Telemetry: feedback submission failure
            metrics.add(metrics.errorEvent({
              name: 'FeedbackSubmissionError',
              message: err?.message || 'Network error submitting feedback',
              stack: err?.stack,
            }));
            metrics.flush(); // Flush immediately — high-value domain error event
          } finally {
            setFeedbackSubmitting(false);
          }
        }}
        metrics-label="Submit to EHR"
        okText="Submit"
        confirmLoading={feedbackSubmitting}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* <div style={{ color: "#666" }}>
            {ratingPending === 1
              ? "Tell us what worked well (optional)"
              : "Tell us what could be better (optional)"}
          </div> */}
          <div style={{ color: "#666" }}>
            Note - Do not record any sensitive/PHI information
          </div>
          <TextArea
            placeholder="Add comments (optional)"
            // value={commentsPending}
            onChange={(e) => setCommentsPending(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default AINotesView2;
